// What survives a reload: one namespaced localStorage blob, plus the country
// index the routing layer needs to frame a /{country}/ URL.
//
// The blob is written whole rather than key-by-key, and read back with every
// field optional — an older blob, or none at all, is a normal case and not an
// error. It is deliberately NOT where the date/mode neutrality flags live: a
// reload of a bare canonical URL must come back bare, so those are derived from
// the URL every time (see lib/route-state.ts).
//
// This used to end by calling syncUrl(), which made "remember this" and "show
// this in the address bar" one operation and meant every caller of persist()
// dragged the routing layer along. They are two mirrors of the same state, so
// they are now two independent subscribers of it.

import { state } from './state.ts';
import type { ViewName, DaySeed, CompareUi } from './state.ts';

const PERSIST_KEY = 'fcc.map.v1';

// The persisted blob, as this version writes it. Every field is optional on the
// way back in.
export interface PersistedState {
  doy?: number;
  bucket?: string;
  variable?: string;
  recipe?: string;
  customOn?: boolean;
  view?: ViewName;
  peaksOpen?: boolean;
  whyExpanded?: boolean;
  daySeed?: DaySeed | null;
  compareFull?: boolean;
  compareUi?: CompareUi;
}

export function persist(): void {
  try {
    localStorage.setItem(
      PERSIST_KEY,
      JSON.stringify({
        doy: state.dateIdx,
        bucket: state.bucket,
        variable: state.variable,
        recipe: state.recipeCanon, // the "My kind of day" recipe, in wire form
        customOn: state.customOn, // whether the custom score has been revealed
        peaksOpen: state.peaksOpen, // whether the "where it peaks" panel is up
        whyExpanded: state.whyExpanded, // whether score details are open, everywhere
        daySeed: state.daySeed, // the day "Find your day" was authored from
        compareFull: state.compare.full, // "in full" is a reader's preference, like whyExpanded
        compareUi: state.compareUi, // what they unfolded on the "at a glance" page
        // Re-open into the map, not a stale city or comparison.
        view: state.view === 'city' || state.view === 'compare' ? 'map' : state.view,
      } satisfies PersistedState),
    );
  } catch {
    /* storage may be unavailable (private mode) — persistence is best-effort */
  }
}

// A restored day seed is taken only if it has the shape a DaySeed has: it is
// read before the map exists, and a malformed one would throw somewhere far
// from here (see bootBegin below).
export function validSeed(x: unknown): DaySeed | null {
  const d = x as DaySeed | null;
  if (!d || typeof d !== 'object') return null;
  if (!Number.isInteger(d.id) || typeof d.name !== 'string' || typeof d.country !== 'string') return null;
  if (typeof d.placeSlug !== 'string' || typeof d.dekad !== 'string' || !/^\d{2}-[123]$/.test(d.dekad)) return null;
  if (typeof d.bucket !== 'string' || typeof d.canon !== 'string') return null;
  if (typeof d.lat !== 'number' || typeof d.lon !== 'number' || !d.conditions || typeof d.conditions !== 'object') return null;
  return d;
}

export function loadPersisted(): PersistedState | null {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
}

// ── a boot that never finished ───────────────────────────────────────────────
//
// Restored state is read before the map exists and feeds everything after it, so
// a value this build no longer understands does not fail where it is read — it
// throws further down main(), which kills every step that had not run yet. The
// symptom is a page that comes up and simply never draws the field: the bundle
// runs, the manifest loads, the viewport queries go out, and the tile requests
// never do.
//
// Nothing in the UI can clear that, so the blob bricks THAT browser until
// somebody opens a console — which is exactly what happened to a desktop Safari
// carrying state from an older build, and the map only came back after
// localStorage.clear(). Reloading does not help: the same blob is read again.
//
// So a boot marks itself in progress before it touches restored state and clears
// the mark once it has finished. Finding the mark still set on the next load
// means the last boot died on the way through: drop the restorable keys and come
// up on defaults. The cost of a false positive is one forgotten day/lens, which
// is worth paying to make a dead app self-heal in a single reload.
const BOOT_KEY = 'fcc.map.booting';

// Everything read during boot that a reader's own history wrote. The days list
// belongs to myday.ts — it is named here because this is the only place that has
// to know the whole set, and a key missing from it is a key that can still brick
// a boot.
const RESTORABLE_KEYS = [PERSIST_KEY, 'fcc.days.v1', 'fcc.last.v1'];

/** Mark a boot as started, discarding restored state if the last one never
 *  finished. Returns true when that discard happened. */
export function bootBegin(): boolean {
  try {
    const crashed = localStorage.getItem(BOOT_KEY) === '1';
    if (crashed) for (const key of RESTORABLE_KEYS) localStorage.removeItem(key);
    localStorage.setItem(BOOT_KEY, '1');
    // Leaving the page mid-boot is not a crash — a reader who closes the tab
    // while the map is still coming up must not lose their state for it.
    // pagehide rather than unload: it is the one Safari fires reliably, bfcache
    // included.
    addEventListener('pagehide', bootDone);
    return crashed;
  } catch {
    return false; // storage unavailable (private mode): nothing to restore, nothing to heal
  }
}

/** Mark the boot as having got all the way through. */
export function bootDone(): void {
  try {
    localStorage.removeItem(BOOT_KEY);
  } catch {
    /* as above */
  }
}

// One country's entry in the deep-link index.
export interface CountryRow {
  name: string;
  slug: string;
  iso2: string;
  bbox: [number, number, number, number];
  center: [number, number];
  pop: number;
}

// The deep-link country index (/api/countries), fetched once and cached. A
// country-only URL uses it to frame the map on that country's bbox. On a
// transient failure the promise is cleared so a later navigation can retry.
let countriesPromise: Promise<CountryRow[]> | null = null;

export function getCountries(): Promise<CountryRow[]> {
  if (!countriesPromise) {
    countriesPromise = fetch('/api/countries')
      .then((r) => (r.ok ? (r.json() as Promise<{ countries?: CountryRow[] } | null>) : null))
      .then((d) => (d && d.countries) || [])
      .catch(() => {
        countriesPromise = null;
        return [];
      });
  }
  return countriesPromise;
}
